import moment from "moment";

import type { QueryFilters } from "src/utils/buildQueryParams";
import type {
  AccountReportDetailedRow,
  SubcontractorReportDetailedRow,
  SubcontractorReportEntryType,
} from "src/services/analytics.types";

type ReportQueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>;

/**
 * Flatten a report query into `buildQueryParams` filters. The backend takes id lists as
 * comma-separated strings, and an empty list means "all", so it is dropped rather than sent.
 */
export const toReportQueryFilters = (query: Record<string, ReportQueryValue>): QueryFilters =>
  Object.entries(query).reduce<QueryFilters>((acc, [key, value]) => {
    if (Array.isArray(value)) {
      if (value.length) acc[key] = value.join(",");
    } else {
      acc[key] = value as Exclude<ReportQueryValue, ReadonlyArray<string | number>>;
    }
    return acc;
  }, {});

export interface ReportRowMeta {
  id: number;
  sl: number;
  formattedDate: string;
}

/**
 * Add the table row id, running serial number (continuing across pages) and a display date.
 * Summary rows have no date, so `formattedDate` is empty for them.
 */
export const withReportRowMeta =
  (offset: number) =>
  <T extends object>(row: T, index: number): T & ReportRowMeta => {
    const date = "date" in row && typeof row.date === "string" ? row.date : null;
    return {
      ...row,
      id: index + 1 + offset,
      sl: index + 1 + offset,
      formattedDate: date ? moment(date).format("DD MMM YYYY") : "",
    };
  };

/** Sum one numeric field over report rows, formatted to 2 decimals for the subtotal row. */
export const sumReportField = <T extends object>(rows: T[], field: keyof T): string =>
  rows.reduce((sum, row) => sum + Number(row[field] ?? 0), 0).toFixed(2);

export interface AccountDetailedTableRow extends AccountReportDetailedRow {
  creditAmount: number;
  debitAmount: number;
  counterpartyLabel: string;
}

/** Split an account entry into In/Out columns; fall back to the category when there is no counterparty account. */
export const toAccountDetailedTableRow = (row: AccountReportDetailedRow): AccountDetailedTableRow => ({
  ...row,
  creditAmount: row.entryType === "credit" ? Number(row.amount) : 0,
  debitAmount: row.entryType === "debit" ? Number(row.amount) : 0,
  counterpartyLabel: row.counterpartyName || row.categoryName || "",
});

const SUBCONTRACTOR_ENTRY_LABELS: Record<SubcontractorReportEntryType, string> = {
  contract: "Contract",
  payment: "Payment",
};

export interface SubcontractorDetailedTableRow extends SubcontractorReportDetailedRow {
  entryLabel: string;
  details: string;
  contractAmount: number;
  paymentAmount: number;
}

/** Contract rows carry a title, payment rows a description; show whichever exists in one column. */
export const toSubcontractorDetailedTableRow = (row: SubcontractorReportDetailedRow): SubcontractorDetailedTableRow => ({
  ...row,
  entryLabel: SUBCONTRACTOR_ENTRY_LABELS[row.entryType] ?? row.entryType,
  details: [row.title, row.description].filter(Boolean).join(" - "),
  contractAmount: Number(row.contractAmount ?? 0),
  paymentAmount: Number(row.paymentAmount ?? 0),
});
